import { logger } from '../logger';

type PluralTranslation = {
  zero?: string;
  one?: string;
  other: string;
};
type TranslationType = string | PluralTranslation;
type Translation = Record<string, TranslationType | undefined>;

export type Language = string;
export type Translations = Record<Language, Translation>;

/**
 * Interpolates a string and appends any key/value pair described in it, e.g
 * "shaggy: {scooby}" with an options object of {scooby: "scrappy"} becomes "shaggy: scrappy"
 */
function interpolate(str: string, options: object) {
  if (Object.keys(options).length > 0 && str) {
    const updatedValue = Object.entries(options).reduce(
      (strVal, [objKey, objValue]) => {
        const isTruthy = Boolean(objValue);
        const isNumber = typeof objValue === 'number';
        const hasValue = isTruthy || isNumber;
        if (typeof strVal === 'string' && hasValue) {
          // objKey comes from Object.entries(options) — controlled i18n token names, not user input.
          return strVal.replace(new RegExp(`{\\s*${objKey}\\s*}`), objValue); // eslint-disable-line security/detect-non-literal-regexp
        }

        return strVal;
      },
      str
    );

    if (typeof updatedValue === 'string') return updatedValue;
  }
  return str;
}

// Docs on pluralization: https://phrase.com/blog/posts/pluralization/
// We shouldn't have to support any more than one and other, and I added zero just for the kicks.
function pluralise({ zero, one, other }: PluralTranslation, options: object) {
  const count = 'count' in options ? options.count : 0;
  if (isNaN(Number(count))) {
    logger.warn(
      'Tried to pluralise without a valid count (needs to be a number)'
    );
  }

  if (count === 0 && zero) {
    return interpolate(zero, options);
  } else if (count === 1 && one) {
    return interpolate(one, options);
  }
  return other ? interpolate(other, options) : undefined;
}

function transformValue(
  key: string,
  translation: Translation,
  options: object
) {
  // key is a controlled i18n translation key, not user input
  // eslint-disable-next-line security/detect-object-injection
  const value = translation[key];

  if (typeof value === 'string') {
    return interpolate(value, options);
  }

  if (value) {
    const pluralisedValue = pluralise(value, options);

    if (pluralisedValue) {
      return pluralisedValue;
    }
  }

  return key;
}

export const locale = (
  key: string,
  selectedLanguage: Language,
  options: object = {},
  fallbackValue = '',
  languages: Translations
): string => {
  if (!Object.keys(languages).includes(selectedLanguage)) {
    // console.warn(`UNSUPPORTED LANGUAGE: ${selectedLanguage}`);
    return key;
  }

  // selectedLanguage was just verified above to be a key of the known languages object
  // eslint-disable-next-line security/detect-object-injection
  const locales = languages[selectedLanguage];
  const fallbackLocales = languages['en'];

  // key is a controlled i18n translation key, not user input
  // eslint-disable-next-line security/detect-object-injection
  if (!locales?.[key]) {
    if (fallbackValue) {
      return fallbackValue;
    }

    logger.warn(`Missing locale for ${key}`);
    return transformValue(key, fallbackLocales || {}, options);
  }
  return transformValue(key, locales, options);
};
